import { scope, type } from "arktype";

// ─────────────────────────────────────────────────────────────────────────────
// Error Codes
// ─────────────────────────────────────────────────────────────────────────────

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

export const ErrorCodes = {
  SESSION_NOT_FOUND: "SESSION_NOT_FOUND",
  INVALID_MESSAGE: "INVALID_MESSAGE",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  SESSION_EXITED: "SESSION_EXITED",
  INVALID_PATH: "INVALID_PATH",
} as const;

export const ErrorCode = type.or(
  ...Object.values(ErrorCodes).map((code) => `'${code}'` as const),
);

/** Terminal dimensions */
export const TerminalSize = type({
  cols: "number",
  rows: "number",
});

export type TerminalSize = typeof TerminalSize.infer;

// ─────────────────────────────────────────────────────────────────────────────
// Terminal Options
// ─────────────────────────────────────────────────────────────────────────────

/** Options for creating a terminal */
export const TerminalOptions = type({
  /** Shell to spawn (default: user's shell or /bin/sh) */
  shell: "string",
  /** Arguments to pass to shell */
  args: "string[]",
  /** Working directory */
  cwd: "string",
  /** Environment variables (merged with process.env) */
  env: { "[string]": "string" },
  /** Initial terminal size */
  size: TerminalSize,
  /** Scrollback buffer size in bytes (default: 100KB) */
  scrollbackBytes: "number",
  /** If true, terminal will not be cleaned up due to keepalive timeout */
  persistent: "boolean",
  /** Optional explicit terminal ID (default: auto-generated) */
  id: "string",
}).partial();

export type TerminalOptions = typeof TerminalOptions.infer;

export type AutoCreateTerminalOptions = Pick<
  TerminalOptions,
  "cwd" | "env" | "id" | "shell" | "size" | "args"
>;

/** Unique identifier for a terminal session */
export const TerminalId = type("string#TerminalId");
export type TerminalId = typeof TerminalId.infer;

// ─────────────────────────────────────────────────────────────────────────────
// Terminal State
// ─────────────────────────────────────────────────────────────────────────────

/** Terminal session state */
export const TerminalState = type.or("'running'", "'exited'", "'killed'");
export type TerminalState = typeof TerminalState.infer;

// ─────────────────────────────────────────────────────────────────────────────
// Terminal Session
// ─────────────────────────────────────────────────────────────────────────────

/** Terminal session metadata (serializable) */
export const TerminalSession = type({
  id: TerminalId,
  state: TerminalState,
  createdAt: "number",
  lastActivityAt: "number",
  exitCode: "number?",
  pid: "number",
  /** If true, terminal will not be cleaned up due to keepalive timeout */
  persistent: "boolean?",
});

export type TerminalSession = typeof TerminalSession.infer;

// ─────────────────────────────────────────────────────────────────────────────
// Client -> Server Messages (JSON, sent as text frames)
// ─────────────────────────────────────────────────────────────────────────────

// Note: Write is handled via binary frames (raw bytes, no framing)

export const { ResizeMessage, KeepaliveMessage, KillMessage, ClientMessage } =
  scope({
    ResizeMessage: {
      type: "'resize'",
      size: TerminalSize,
    },
    KeepaliveMessage: {
      type: "'keepalive'",
    },
    KillMessage: {
      type: "'kill'",
      signal: "string?",
    },
    ClientMessage: "ResizeMessage | KeepaliveMessage | KillMessage",
  }).export();

export type ClientMessage = typeof ClientMessage.infer;

export type ResizeMessage = typeof ResizeMessage.infer;
export type KeepaliveMessage = typeof KeepaliveMessage.infer;
export type KillMessage = typeof KillMessage.infer;

// ─────────────────────────────────────────────────────────────────────────────
// Server -> Client Messages (JSON, sent as text frames)
// ─────────────────────────────────────────────────────────────────────────────

// Note: Data is handled via binary frames (raw bytes, no framing)

export const { ConnectedMessage, ExitMessage, ErrorMessage, ServerMessage } =
  scope({
    /** Sent immediately after WebSocket connection is established */
    ConnectedMessage: {
      type: "'connected'",
      session: TerminalSession,
    },
    ExitMessage: {
      type: "'exit'",
      exitCode: "number",
    },
    ErrorMessage: {
      type: "'error'",
      code: ErrorCode,
      message: "string",
    },
    ServerMessage: "ConnectedMessage | ExitMessage | ErrorMessage",
  }).export();

export type ConnectedMessage = typeof ConnectedMessage.infer;
export type ExitMessage = typeof ExitMessage.infer;
export type ErrorMessage = typeof ErrorMessage.infer;
export type ServerMessage = typeof ServerMessage.infer;
